import React, { useEffect, useMemo } from 'lib/react';
import {
  EVGrid, EVTypography,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { useSelector, useDispatch } from 'lib/redux';
import { MULTIFAMILY, NEW_UNIFIED_SKUS, PRODUCT_IDS } from 'constants/AppConstants';
import { LOAD_USER_PRODUCTS } from 'constants/ActionTypes';
import { groupProductsIntoCategories } from 'features/order/components/Product/ProductList.util';
import { cancelOrderPreferencesChanges } from 'features/orderPreferences/OrderPreferences.actions';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import RetryScreen from 'commonComponents/retry-screen';
import ProductSelectionStyles from './ProductSelectionStyles';
import StructureType from './components/StructureType.component';
import { STRUCTURE_TYPES } from './ProductSelection.utils';
import ProductSelectionElement from './components/ProductSelectionElement.component';
import ProductSelectionMeasurement from './components/ProductSelectionMeasurement.component';
import EmailRecipients from './components/emailRecipients/EmailRecipients.component';

function ProductSelection() {
  const { t } = useTranslation();
  const classes = ProductSelectionStyles();
  const dispatch = useDispatch();
  const {
    preferences, userProducts, loading: { userProducts: userProductsLoading }, error: { userProducts: userProductsError },
  } = useSelector(({ userData }) => ({
    preferences: userData.orderPreferences,
    userProducts: userData.userProducts,
    loading: userData.loading,
    error: userData.error,
  }));

  const orderPreferences = useSelector(({ userData }) => userData.orderPreferences);
  const inputFieldValues = useSelector(({ orderPreference }) => orderPreference.inputFieldValues);
  const propertyType = STRUCTURE_TYPES[getPropertyValues(preferences, inputFieldValues, 'structureType').value || 1];

  useEffect(
    () => () => dispatch(cancelOrderPreferencesChanges()),
    [],
  );

  if (userProductsError) {
    return (
      <RetryScreen
        elevation={0}
        handleRetryButtonClick={() => {
          dispatch({ type: LOAD_USER_PRODUCTS });
        }}
        className={classes.pageContainerRetry}
      />
    );
  }

  const roofProduct = getPropertyValues(orderPreferences, inputFieldValues, 'roofProduct');
  const wallWinDoorProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallWinDoorProduct');
  const wallsFKALiteProduct = getPropertyValues(orderPreferences, inputFieldValues, 'wallsFKALiteProduct');

  const isNewSKUSelected = NEW_UNIFIED_SKUS.includes(roofProduct?.value)
    || wallWinDoorProduct?.value === PRODUCT_IDS.WallWinDoor
    || wallsFKALiteProduct?.value === PRODUCT_IDS.WallsFKALite;

  const products = useMemo(() => {
    // #genai: use optional chaining (Sonar S6582); userProducts/NotSet are either objects or null/undefined, so semantics are unchanged
    if (!userProductsLoading && userProducts?.[propertyType]) {
      const propertyTypeProducts = [
        ...userProducts[propertyType].PrimaryProducts,
        ...((propertyType !== MULTIFAMILY && userProducts.NotSet?.PrimaryProducts) ? userProducts.NotSet.PrimaryProducts : []),
      ];
      const { solar, ...productCategoriesList } = groupProductsIntoCategories(propertyTypeProducts)(false);
      return { ...productCategoriesList, solar };
    }
    return null;
  }, [userProductsLoading, propertyType, userProducts]);

  return (
    <EVGrid className={classes.productSelectionContainer}>
      <EVGrid className={classes.productSelectionHeaderContainer}>
        <EVTypography className={classes.productSelectionHeaderTitle}>{t('order_preferences__product_selection_title')}</EVTypography>
        <EVTypography className={classes.productSelectionHeaderDesc}>{t('order_preferences__product_selection_desc')}</EVTypography>
      </EVGrid>
      {(!isNewSKUSelected) && (
        <EVGrid className={classes.productSelectionContentContainer}>
          <EVGrid className={classes.structureTypeContentBox}>
            <EVTypography className={classes.structureTypeTitle}>{t('order_preferences__product_selection_structure_type')}</EVTypography>
            <StructureType />
          </EVGrid>
        </EVGrid>
      )}
      <EVGrid className={classes.productsAndDeliveryContentBox}>
        <EVTypography className={classes.structureTypeTitle}>{t('order_preferences__products_and_delivery')}</EVTypography>
        <EVGrid className={classes.productSelectionElementContainer}>
          <ProductSelectionElement
            products={products || {}}
          />
        </EVGrid>
      </EVGrid>
      <ProductSelectionMeasurement />
      <EmailRecipients />
    </EVGrid>
  );
}
export default ProductSelection;
